import React from 'react';
import { Wand2, Sparkles, Zap, Download, History, Shield, Palette, Layers, RefreshCw, Image, FileCheck, CloudUpload, Lock, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const Features = () => {
  const navigate = useNavigate();

  const coreFeatures = [
    {
      icon: Wand2,
      title: 'AI-Powered Generation',
      description: 'Transform your ideas into stunning T-shirt designs using cutting-edge AI technology powered by Ideogram and ChatGPT.',
      gradient: 'from-cyan-500 to-blue-500',
      benefits: ['Natural language prompts', 'Advanced AI models', 'Creative interpretation', 'Style variety'],
    },
    {
      icon: Sparkles,
      title: 'Automatic Background Removal',
      description: 'Get crystal-clear, print-ready PNG files with transparent backgrounds automatically using PhotoRoom technology.',
      gradient: 'from-turquoise-500 to-teal-500',
      benefits: ['Professional quality', 'Instant processing', 'Perfect edges', 'Print-ready files'],
    },
    {
      icon: History,
      title: 'Complete Version History',
      description: 'Every iteration is saved automatically. Browse through all versions and never lose a great design.',
      gradient: 'from-blue-500 to-indigo-500',
      benefits: ['Up to 10 versions', 'Auto-save', 'Easy browsing', 'Version comparison'],
    },
    {
      icon: RefreshCw,
      title: 'Smart Main Version',
      description: 'Select any version as your main design. Perfect for A/B testing or keeping your best work easily accessible.',
      gradient: 'from-purple-500 to-pink-500',
      benefits: ['One-click selection', 'Easy switching', 'Clear labeling', 'Quick access'],
    },
    {
      icon: Download,
      title: 'HD Downloads',
      description: 'Export high-resolution images optimized for any print-on-demand platform or commercial use.',
      gradient: 'from-green-500 to-emerald-500',
      benefits: ['High resolution', 'Multiple formats', 'Print quality', 'Commercial ready'],
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Generate professional-grade designs in 30-60 seconds. No waiting, no delays - just instant creativity.',
      gradient: 'from-yellow-500 to-orange-500',
      benefits: ['30s generation', 'Real-time updates', 'Fast editing', 'Instant downloads'],
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Enterprise-grade security with Firebase keeps your creative assets safe, secure, and completely private.',
      gradient: 'from-red-500 to-pink-500',
      benefits: ['Cloud backup', 'Data encryption', 'Private storage', 'Secure access'],
    },
    {
      icon: Palette,
      title: 'Multiple Aspect Ratios',
      description: 'Create designs in various sizes - square, wide, tall, or classic formats to fit any use case.',
      gradient: 'from-cyan-500 to-turquoise-500',
      benefits: ['1:1 Square', '16:9 Wide', '9:16 Tall', '4:3 Classic'],
    },
    {
      icon: Layers,
      title: 'Project Management',
      description: 'Organize all your designs in one place. Each project maintains its own version history and settings.',
      gradient: 'from-indigo-500 to-purple-500',
      benefits: ['Easy organization', 'Quick search', 'Project folders', 'Bulk operations'],
    },
  ];

  const advancedFeatures = [
    {
      icon: CloudUpload,
      title: 'Cloud Storage',
      description: 'All your designs are securely stored in the cloud and accessible from any device.',
    },
    {
      icon: Lock,
      title: 'Commercial Licensing',
      description: 'Elite plan includes full commercial rights to use and sell your AI-generated designs.',
    },
    {
      icon: Users,
      title: 'Community Showcase',
      description: 'Get inspired by designs from our creator community (optional sharing).',
    },
    {
      icon: TrendingUp,
      title: 'Usage Analytics',
      description: 'Track your design generation, credit usage, and creative productivity.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Features - Fluxara</title>
        <meta name="description" content="Discover all the powerful features that make Fluxara the best AI T-shirt design platform." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 gradient-mesh">
        {/* Hero */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500 to-turquoise-500 rounded-3xl mb-6 shadow-2xl">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              Powerful <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600">Features</span>
              <br />for Every Creator
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to create, iterate, and perfect your T-shirt designs with AI
            </p>
          </div>
        </section>

        {/* Core Features Grid */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="glass border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2 group">
                    <CardContent className="p-8">
                      <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                      <div className="space-y-2">
                        {feature.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                            <span>{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Advanced Features */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                And There's <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-cyan-400">More...</span>
              </h2>
              <p className="text-xl text-gray-300">
                Additional features to power your creative workflow
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advancedFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="glass-dark p-6 rounded-2xl hover:scale-105 transition-transform duration-300">
                    <Icon className="w-10 h-10 text-cyan-400 mb-4" />
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-gray-300 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-turquoise-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-6">Ready to Experience All Features?</h2>
            <p className="text-xl mb-8 text-cyan-100">
              Start creating for free - no credit card required
            </p>
            <Button
              size="lg"
              className="text-xl px-12 py-8 bg-white text-cyan-900 hover:bg-gray-100 shadow-2xl font-bold"
              onClick={() => navigate('/generate')}
            >
              <Sparkles className="mr-3 w-6 h-6" />
              Get Started Free
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Features;
